import { useCallback, useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { StationeryTemplateCategoryEnum } from '@graphql/generated';
import { Highlight, ProductOffering } from './ConciergeServiceSection.types';
import { ConciergeServiceCategory } from '@apps/card/routes/Concierge/config';
import { INVITATION_IDS, SAVE_THE_DATE_IDS } from '@apps/card/routes/Dashboard/components/ConciergeServiceSection/ConciergeServiceSection.utils';

export const useProductOfferings = (): {
  productOfferings: Record<ConciergeServiceCategory, ReadonlyArray<ProductOffering>>;
  getConciergeCategoryFromDesignSetName: (designSetName: string) => ConciergeServiceCategory;
  getProductOfferingFromDesignSetName: (designSetName: string) => ProductOffering;
  getProductOfferingFromCategoryAndId: (category: ConciergeServiceCategory, id: string) => ProductOffering | undefined;
} => {
  const { tq } = useTranslation('stationery');

  const productOfferings = useMemo(() => {
    const highlights = (...items: ReadonlyArray<Highlight>): ReadonlyArray<Highlight> => items;

    const sharedPersonalization: Highlight = { text: tq`Personalization with Joy concierge design services` };
    const sharedEnvelopeAddressing: Highlight = { text: tq`Recipient and return address printing` };
    const sharedNotIncluded: Highlight = {
      text: tq`Photographed design suggestions such as ribbons, wax seals, string, etc are not included unless specifically stated`,
    };

    const invitationOfferings: ReadonlyArray<ProductOffering> = [
      {
        id: INVITATION_IDS.julyTrifold.id,
        identifier: INVITATION_IDS.julyTrifold.identifier,
        shortName: INVITATION_IDS.julyTrifold.shortName,
        longName: INVITATION_IDS.julyTrifold.longName,
        costPerHundredString: '$4.70',
        // TODO: add video and thumbnail once available
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/july-trifold/july-trifold-1.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`The ${INVITATION_IDS.julyTrifold.shortName} invitation blends soft duck-egg blue artwork and classic typography for a refined, romantic look.` },
          { text: tq`Three-panel, arch-top trifold keeps your invitation, details and RSVP neatly in one all-in-one format.` },
          { text: tq`Printed on 300 gsm Matte Paper for a smooth, luxurious feel and crisp, high-quality reproduction of the design.` },
          { text: tq`Includes a complimentary envelope in your choice of Almond or Desert Blue to complete the suite.` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Trifold wedding invitation card, 5" x 16" (5" x 7" folded) on 300 GSM almond colored paper.` },
          { text: tq`Premium almond or desert blue color envelopes.` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: INVITATION_IDS.wildestDream.id,
        identifier: INVITATION_IDS.wildestDream.identifier,
        shortName: INVITATION_IDS.wildestDream.shortName,
        longName: INVITATION_IDS.wildestDream.longName,
        costPerHundredString: '$10.73',
        videoUrl: 'https://player.vimeo.com/video/947047706?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1853442320-6f5b0b94f1e861bc7d5b8da0d7f23132d42880f26d2f5821f02ff6287c29c357-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/wildest-dreams/wildest-dreams-suite-1-main.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`Immerse yourself in the timeless allure of the ${INVITATION_IDS.wildestDream.shortName} invitation set` },
          { text: tq`Customize the information card with the option to add your website or RSVP QR code` },
          { text: tq`Each invitation is encased in a transparent delicate floral print vellum bellyband` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Gold foil invitation card, 5”x 7“ on premium 380GSM paper` },
          { text: tq`Details card, 4.2” x 5.5” on premium 380GSM paper` },
          { text: tq`Vellum bellyband 80GSM ` },
          { text: tq`Premium white envelope` },
          sharedEnvelopeAddressing,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: INVITATION_IDS.together.id,
        identifier: INVITATION_IDS.together.identifier,
        shortName: INVITATION_IDS.together.shortName,
        longName: INVITATION_IDS.together.longName,
        costPerHundredString: '$7.40',
        videoUrl: 'https://player.vimeo.com/video/947047579?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1853442234-787d4b800726a285c5dd2edc49aaab297f6efaa18a921d09b01bb853f971a48e-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/together-gatefold/together-gatefold-1-main.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`Timeless photo gatefold invitation includes all the information for guests on one folded invitation` },
          { text: tq`This bundle includes a delicate vellum bellyband to wrap around your invitations for a finished look` },
          { text: tq`Wax seals are not included, but can easily be purchased from suggested vendors` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Photo gatefold wedding invitation card, 10” x 7” on 380 GSM premium paper` },
          { text: tq`Vellum bellyband 80GSM` },
          { text: tq`Premium white envelope` },
          sharedEnvelopeAddressing,
        ),
        printingBusinessDaysQuantity: 2,
      },
      {
        id: INVITATION_IDS.sofia.id,
        identifier: INVITATION_IDS.sofia.identifier,
        shortName: INVITATION_IDS.sofia.shortName,
        longName: INVITATION_IDS.sofia.longName,
        costPerHundredString: '$9.64',
        videoUrl: 'https://player.vimeo.com/video/947047427?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1853441762-3cc48466d6550ed05108cb8f662fbd8e8446469012b444360a6f7961d28f950f-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/sofia/sofia-1-main.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`Eye catching modern suite printed on shimmery metallic paper for a subtle glow` },
          { text: tq`Matching arch shaped wedding information card that fits perfectly with the main invitation` },
          { text: tq`A small landscape arch RSVP card, with the option to add your wedding website QR code` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Wedding invitation card, 5” x 7” arch on 300 GSM metallic paper` },
          { text: tq`Details card, 5.8” x 3.7” arch on 300 GSM metallic paper` },
          { text: tq`RSVP card, 4.2” x 2.1” landscape arch on 300 GSM metallic paper` },
          { text: tq`Premium almond color envelope` },
          sharedEnvelopeAddressing,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: INVITATION_IDS.sage.id,
        identifier: INVITATION_IDS.sage.identifier,
        shortName: INVITATION_IDS.sage.shortName,
        longName: INVITATION_IDS.sage.longName,
        costPerHundredString: '$12.20',
        videoUrl: 'https://player.vimeo.com/video/947047267?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1853441651-c49e0f48614b2978086924dd41716baf10bdeced9b21d1a967e2be77542f22d1-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/sage/sage-1-main.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`Deep forest colors combined with elegant neutrals combine for a refined but natural look` },
          { text: tq`Each set comes with gold paper clips to keep all your cards together` },
          { text: tq`Includes premium almond color envelopes included` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Wedding invitation card, 5” x 7” arch on Forest Green 300GSM paper with white ink` },
          { text: tq`Details card, 4.2” x 5.5” curved edge on 300 GSM Almond Paper` },
          { text: tq`RSVP card, 4.2” x 2.1” land scape arch on 300GSM paper` },
          { text: tq`Premium Almond Envelope` },
          { text: tq`Gold color paper clip` },
          sharedEnvelopeAddressing,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: INVITATION_IDS.milkAndHoney.id,
        identifier: INVITATION_IDS.milkAndHoney.identifier,
        shortName: INVITATION_IDS.milkAndHoney.shortName,
        longName: INVITATION_IDS.milkAndHoney.longName,
        costPerHundredString: '$9.33',
        videoUrl: 'https://player.vimeo.com/video/947047142?badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1853441650-1e9eab448236c13eb967d5aa9eb793b23c6f4b09754dc3e4739d10c4c05017d3-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/milk-and-honey/milk-and-honey-1-main.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`Earthy tone invitation package perfect for those wanting neutral colors` },
          { text: tq`The wedding details card features two-tone front and back with for wedding details and RSVP information` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Wedding invitation card, 5” x 7”, on 300GSM cotton` },
          { text: tq`Wedding details card, 5” x 7” half arch on 300 GSM almond paper` },
          { text: tq`Bellyband 120 GSM matte` },
          { text: tq`Premium almond color envelopes with options to select to grey, or harvest` },
          sharedEnvelopeAddressing,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: INVITATION_IDS.jardin.id,
        identifier: INVITATION_IDS.jardin.identifier,
        shortName: INVITATION_IDS.jardin.shortName,
        longName: INVITATION_IDS.jardin.longName,
        costPerHundredString: '$6.33',
        videoUrl: 'https://player.vimeo.com/video/947046975?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1853441362-42f030c8a90dd23ddd39ced311850e1e069f336c0c1fb00f0bd88d70c72ea8c0-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/jardin/jardin-1-main.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`The ${INVITATION_IDS.jardin.shortName} gatefold invitation is for those flower and nature lovers, sure to set the tone for your special day` },
          { text: tq`This all-in-one wedding invitation includes all your wedding day finer details and RSVP information` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Gatefold wedding invitation card, 10” x 7” on 380GSM premium paper` },
          { text: tq`Premium envelopes in forest green or peach` },
          sharedEnvelopeAddressing,
        ),
        printingBusinessDaysQuantity: 2,
      },
      {
        id: INVITATION_IDS.ivory.id,
        identifier: INVITATION_IDS.ivory.identifier,
        shortName: INVITATION_IDS.ivory.shortName,
        longName: INVITATION_IDS.ivory.longName,
        costPerHundredString: '$12.45',
        videoUrl: 'https://player.vimeo.com/video/947046859?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1853441091-25ae32d4ca89fb7e08092400588fbbe28ee9a7d9df56b0fdc69171f18f7a60c6-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/ivory/ivory-1-main.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`This set includes arch shaped invitations printed on beautiful cotton paper` },
          { text: tq`Matching double-sided information card to include all your wedding day details` },
          { text: tq`A small landscape arch shape RSVP card, with the option to add your wedding website QR code,` },
          { text: tq`Premium black color envelopes included` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Wedding invitation card, 5” x 7” arch on premium 300 GSM cotten paper` },
          { text: tq`Details card,  4.2” x 5.5” rounded edge on 300 GSM almond paper` },
          { text: tq`RSVP card, 4.2” x 2.1” landscape arch on 270 GSM black paper with white ink` },
          { text: tq`Premium black envelopes with white ink` },
          sharedEnvelopeAddressing,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: INVITATION_IDS.harmony.id,
        identifier: INVITATION_IDS.harmony.identifier,
        shortName: INVITATION_IDS.harmony.shortName,
        longName: INVITATION_IDS.harmony.longName,
        costPerHundredString: '$12.71',
        videoUrl: 'https://player.vimeo.com/video/947046740?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1853440971-50a8a1d4d9d5983e2ca0ee4b88d058f6e4633d9d99213e76dbe554008410e499-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/harmony-suite/harmony-suite-1-main.jpg?v=2',
        descriptionHighlights: highlights(
          {
            text: tq`The ${INVITATION_IDS.harmony.shortName} set is a perfect balance between wedding neutrals and greenery. With stunning script fonts and quality print and paper, this layered invitation set is bound to set the tone for your big d`,
          },
          { text: tq`Matching double-sided information card that is big enough for all your wedding details` },
          { text: tq`Small green name card that can be used as a guest name card to tie in a hint of green to this suit` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Wedding invitation card, 5”x7” arch on Premium 300 GSM Cotten Paper` },
          { text: tq`Details card,  4.2” x 5.5” half arch on 300 GSM cotton paper` },
          { text: tq`Guest name card,  3.1” x 3.7”` },
          { text: tq`Premium grey envelopes` },
          sharedEnvelopeAddressing,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: INVITATION_IDS.harmonyGatefold.id,
        identifier: INVITATION_IDS.harmonyGatefold.identifier,
        shortName: INVITATION_IDS.harmonyGatefold.shortName,
        longName: INVITATION_IDS.harmonyGatefold.longName,
        costPerHundredString: '$6.33',
        videoUrl: 'https://player.vimeo.com/video/947044716?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1853438016-64478fea48afc207f85486f7ad8248b0412f8a688d61e3e4c10d91036a8c4013-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/harmony-gatefold/harmony-gatefold-1-main.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`The ${INVITATION_IDS.harmonyGatefold.shortName} invitations are a perfect way to send your guests all the info they need for the day` },
          { text: tq`High quality photo printing on the outside` },
          { text: tq`The inside of this all in one gatefold invitation includes all your wedding day finer details, RSVP, and wedding timeline` },
          { text: tq`Harvest color envelopes included with each invitation` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Gatefold Wedding Invitation, 10” x 7” on premium 380 GSM paper` },
          { text: tq`Premium harvest color envelopes` },
          sharedEnvelopeAddressing,
        ),
        printingBusinessDaysQuantity: 2,
      },
      {
        id: INVITATION_IDS.empireNow.id,
        identifier: INVITATION_IDS.empireNow.identifier,
        shortName: INVITATION_IDS.empireNow.shortName,
        longName: INVITATION_IDS.empireNow.longName,
        costPerHundredString: '$4.26',
        videoUrl: 'https://player.vimeo.com/video/947044156?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1853437219-35b71461829d0e6f7be76a171df3842831ec96090781b290714cc573a26e43c1-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/empire-now/empire-now-gatefold.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`The ${INVITATION_IDS.empireNow.shortName} all-in-one Gatefold design is a combination of curvy die-cut and elegant typography` },
          { text: tq`This all-in-one wedding invitation with high quality photo printing includes all your wedding day finer details and RSVP information in one elegant card` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Gatefold Wedding Invitation, 11.8” x 7” side wave on premium 380 GSM paper` },
          { text: tq`Premium black envelopes with white ink` },
          sharedEnvelopeAddressing,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: INVITATION_IDS.byronGatefold.id,
        identifier: INVITATION_IDS.byronGatefold.identifier,
        shortName: INVITATION_IDS.byronGatefold.shortName,
        longName: INVITATION_IDS.byronGatefold.longName,
        costPerHundredString: '$6.33',
        videoUrl: 'https://player.vimeo.com/video/947043616?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1853436469-c1c8a5f3e50f5a917876fdbd74b67dd613cb6505c14702b1c5a0dd0437820abd-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/byron-gatefold/byron-gatefold.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`All your invitation, RSVP, and wedding details in one gatefold card` },
          { text: tq`Customize with your own photo on the inside` },
          { text: tq`This all-in-one wedding invitation includes all your wedding day finer details and RSVP information` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Gatefold Wedding Invitation, 10” x 7” on premium 380 GSM paper` },
          { text: tq`Premium almond color envelopes` },
          sharedEnvelopeAddressing,
        ),
        printingBusinessDaysQuantity: 2,
      },
      {
        id: INVITATION_IDS.celadon.id,
        identifier: INVITATION_IDS.celadon.identifier,
        shortName: INVITATION_IDS.celadon.shortName,
        longName: INVITATION_IDS.celadon.longName,
        costPerHundredString: '$7.37',
        videoUrl: 'https://player.vimeo.com/video/1002121670?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1918422896-2f6428fe3575721e753f510646ae83ec1a3f2d81f9cb31527eee26fbc7b723e0-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/celadon/celadon-1.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`The ${INVITATION_IDS.celadon.shortName} Gatefold design is a combination of curvy die-cut and elegant typography` },
          { text: tq`This all-in-one wedding invitation includes all your wedding day finer details and RSVP information in one elegant card` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Printed in white ink on rich and vibrant Forest Green paper` },
          { text: tq`Gatefold Wedding Invitation, 11.8” x 7” side wave on premium 300 GSM paper` },
          { text: tq`Premium almond color envelopes` },
          sharedEnvelopeAddressing,
          { text: tq`Gold color paper clip` },
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: INVITATION_IDS.wildMeadow.id,
        identifier: INVITATION_IDS.wildMeadow.identifier,
        shortName: INVITATION_IDS.wildMeadow.shortName,
        longName: INVITATION_IDS.wildMeadow.longName,
        costPerHundredString: '$9.07',
        videoUrl: 'https://player.vimeo.com/video/1002121907?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1918423147-1c4cdcfd97be9311d442a0c54da8d65acee0db54f360560e92b9e91bea3e7373-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/wild-meadow/wild-meadow.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`The ${INVITATION_IDS.wildMeadow.shortName} gatefold invitation is perfect for flower lovers and sure to set the tone for your special day` },
          { text: tq`This all-in-one wedding invitation includes all your wedding day finer details and RSVP information` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Gatefold wedding invitation card, 10” x 7” on 380 GSM premium paper` },
          { text: tq`Kraft bellyband, 10.5” x 2” 80 GSM` },
          { text: tq`Premium almond color envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 2,
      },
      {
        id: INVITATION_IDS.eden.id,
        identifier: INVITATION_IDS.eden.identifier,
        shortName: INVITATION_IDS.eden.shortName,
        longName: INVITATION_IDS.eden.longName,
        costPerHundredString: '$8.28',
        videoUrl: 'https://player.vimeo.com/video/1002122162?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1918423449-e2cd083a494ea391fea3cbe969774e9083d8f6219e74cb5050701112f10853df-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/eden/eden.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`The ${INVITATION_IDS.eden.shortName} gatefold invitation is for flower and nature lovers, sure to set the tone for your special day` },
          { text: tq`This all-in-one wedding invitation includes space for all your wedding day detail, RSVP, and wedding day timeline information` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Gatefold wedding invitation card, 10” x 7” on 380 GSM premium paper` },
          { text: tq`Premium almond color envelopes` },
          { text: tq`Delicate Leaves envelope liners` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 2,
      },
      {
        id: INVITATION_IDS.luminous.id,
        identifier: INVITATION_IDS.luminous.identifier,
        shortName: INVITATION_IDS.luminous.shortName,
        longName: INVITATION_IDS.luminous.longName,
        costPerHundredString: '$9.15',
        videoUrl: 'https://player.vimeo.com/video/1002122372?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1918423640-11686ae80aff6e9d9f2b0dbc78b8e3007543c0c0115f63decb19ef4ed02528bc-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/luminous/luminous.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`Immerse yourself in the timeless allure of this ${INVITATION_IDS.luminous.shortName} invitation set with foil-printing` },
          { text: tq`Paired with a delicate transparent vellum info card` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Wedding invitation card, 5” x 7”, on 380 GSM premium paper` },
          { text: tq`Details card, 4.2” x 5.5,” on 180 GSM Vellum with white ink` },
          { text: tq`Premium almond color envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 2,
      },
      {
        id: INVITATION_IDS.graceTrifold.id,
        identifier: INVITATION_IDS.graceTrifold.identifier,
        shortName: INVITATION_IDS.graceTrifold.shortName,
        longName: INVITATION_IDS.graceTrifold.longName,
        costPerHundredString: '$7.22',
        videoUrl: 'https://player.vimeo.com/video/1002122659?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1918423958-cc1c0364f8913c90d260424be616366337c6319be0f4c93c493ad3102085ec38-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/grace-trifold/grace-trifold-1.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`A unique arch-shaped trifold with elegant typography and space for all your wedding details` },
          { text: tq`This all-in-one wedding invitation includes a detachable RSVP card` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Trifold wedding invitation card, 5” x 16.5” (5” x 7” folded) on 300 GSM almond colored paper` },
          { text: tq`Premium almond color envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: INVITATION_IDS.flutter.id,
        identifier: INVITATION_IDS.flutter.identifier,
        shortName: INVITATION_IDS.flutter.shortName,
        longName: INVITATION_IDS.flutter.longName,
        costPerHundredString: '$14.14',
        videoUrl: 'https://player.vimeo.com/video/1002122888?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1918424244-443431f812254ce2f6b8808bea5253082cd7034e1fcc434121223dba98d2f23d-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/flutter/flutter-1.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`Vibrant red and blush tones invitation suite is perfect for those wanting a pop of color` },
          { text: tq`This set includes a wedding invitation, wedding info card with RSVP details, and an extra card on top customized with a favorite quote or your names` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Wedding invitation card, 5” x 7” on Blush 300 GSM paper` },
          { text: tq`Details and RSVP card, 4.3” x 7” side wave edge on Red 270 GSM paper with white ink` },
          { text: tq`Top card, 3.7” x 7” side wave edge on 300 GSM cotton paper` },
          { text: tq`Premium white envelopes` },
          sharedEnvelopeAddressing,
          { text: tq`Gold color paper clip` },
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: INVITATION_IDS.koffee.id,
        identifier: INVITATION_IDS.koffee.identifier,
        shortName: INVITATION_IDS.koffee.shortName,
        longName: INVITATION_IDS.koffee.longName,
        costPerHundredString: '$13.02',
        videoUrl: 'https://player.vimeo.com/video/1002123089?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1918424442-802f112f404bfdfdfebcd874365a398b0e2fd3a24df1f15f0228c69a26944cc2-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/koffee/koffee-1.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`Rich browns and creams combine in this earth-toned invitation suite perfect for a neutral color palette` },
          { text: tq`Share the event details and  timeline with your guests across three layers of cards` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Wedding invitation card, 5” x 7” on 300 GSM harvest color paper with white ink` },
          { text: tq`Program card,  4.3” x 7” side arch on 300 GSM almond color paper` },
          { text: tq`Info and RSVP card, 3.7” x 5.8” side arch on 300 GSM cotton paper` },
          { text: tq`Premium almond color envelopes` },
          sharedEnvelopeAddressing,
          { text: tq`Gold color paper clip` },
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: INVITATION_IDS.grace.id,
        identifier: INVITATION_IDS.grace.identifier,
        shortName: INVITATION_IDS.grace.shortName,
        longName: INVITATION_IDS.grace.longName,
        costPerHundredString: '$15.44',
        videoUrl: 'https://player.vimeo.com/video/1002123486?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1918424894-895a879196f69e937bad2eed2d096867f77fdaae263ed53ef54379fc5d42aae5-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/grace/grace-1.jpg?v=2',
        descriptionHighlights: highlights(
          {
            text: tq`The ${INVITATION_IDS.grace.shortName} set is a perfect balance between wedding neutrals and greenery. With stunning script fonts and quality print and paper, this layered invitation set is bound to set the tone for your big day`,
          },
          { text: tq`Matching double-sided information card that is big enough for all your wedding and RSVP details` },
          { text: tq`Small grey name card that can be used as a guest name card and as a place card for your on-the-day stationery` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Wedding invitation card, 5” x 7” arch on premium 300 GSM seedling color paper with white ink` },
          { text: tq`Details card,  3.7” x 5.7” arch on 300 GSM almond color paper` },
          { text: tq`Guest name card,  3.1” x 3.7” half arch on 300 GSM grey color paper with white ink` },
          { text: tq`Premium seedling green color envelopes` },
          sharedEnvelopeAddressing,
          { text: tq`Gold color paper clip` },
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: INVITATION_IDS.roseEtGris.id,
        identifier: INVITATION_IDS.roseEtGris.identifier,
        shortName: INVITATION_IDS.roseEtGris.shortName,
        longName: INVITATION_IDS.roseEtGris.longName,
        costPerHundredString: '$12.75',
        videoUrl: 'https://player.vimeo.com/video/1001767624?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1918024755-a3e71a0f1a6316b246394a620c45c40c4a5af2fd010ebe0c52cffc7a3a04788b-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/invitation-suites/rose-et-gris/rose-et-gris-1.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`Immerse yourself in the timeless allure of this ${INVITATION_IDS.roseEtGris.shortName} invitation suite with gold foil lettering` },
          { text: tq`Opt for blush RSVP post-cards with your address or share a QR code for digital RSVP` },
          {
            text: tq`An information card in an elegant shade of grey for all the finer details`,
          },
          { text: tq`A matching foil printed bellyband to tie it all together` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Wedding invitation card, 5” x 7” on premium 380 GSM paper with gold foil` },
          { text: tq`Details card,  4.2” x 5.5” on 300 GSM grey color paper` },
          { text: tq`RSVP card,  5.5” x 4.2” on 300 GSM blush color paper` },
          { text: tq`Bellyband, 10.5” x 2” on 120 GSM with gold foil` },
          { text: tq`Premium white envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
    ].sort(() => Math.random() - 0.5);

    const saveTheDateOfferings: ReadonlyArray<ProductOffering> = [
      {
        id: SAVE_THE_DATE_IDS.luna.id,
        identifier: SAVE_THE_DATE_IDS.luna.identifier,
        shortName: SAVE_THE_DATE_IDS.luna.shortName,
        longName: SAVE_THE_DATE_IDS.luna.longName,
        costPerHundredString: '$3.79',
        videoUrl: 'https://player.vimeo.com/video/1006745499?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1923505068-b3897f91cf25802ae46805cb1a9bcc8d129f69f3d4603a208e96bb76cb741216-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/save-the-date-suites/luna/luna-1.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`A blend of gold foil and digital printing, this arch-shaped Save the Date is perfect for setting the tone of your special day. ` },
          { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Save the Date card, 4.2” x 5.5” arch on luxe 380 GSM paper with gold foil` },
          { text: tq`Premium white envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: SAVE_THE_DATE_IDS.linear.id,
        identifier: SAVE_THE_DATE_IDS.linear.identifier,
        shortName: SAVE_THE_DATE_IDS.linear.shortName,
        longName: SAVE_THE_DATE_IDS.linear.longName,
        costPerHundredString: '$3.68',
        videoUrl: 'https://player.vimeo.com/video/1006745515?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1923505088-f3792b40df36dcf7a65e897aec116a73fe169d0f99c98696e83c24d4521faafa-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/save-the-date-suites/linear/linear-1.jpg?v=2',
        descriptionHighlights: highlights(
          {
            text: tq`Modern typography meets premium cotton paper. The ${SAVE_THE_DATE_IDS.linear.shortName} is all about simplicity and elegance to set the tone for your big day.`,
          },
          { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Save the Date card, 4.2” x 5.5” on 300 GSM cotton paper` },
          { text: tq`Premium almond envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 2,
      },
      {
        id: SAVE_THE_DATE_IDS.minimalisme.id,
        identifier: SAVE_THE_DATE_IDS.minimalisme.identifier,
        shortName: SAVE_THE_DATE_IDS.minimalisme.shortName,
        longName: SAVE_THE_DATE_IDS.minimalisme.longName,
        costPerHundredString: '$3.79',
        videoUrl: 'https://player.vimeo.com/video/1006745485?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1923505048-4d254e0268e9772b6ef310131906f0887f52642d986b50a1040a9155fecef66b-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/save-the-date-suites/minimalisme/minimalisme-1.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`Personalized photo cards are the perfect way to announce your big day.` },
          { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Save the Date card, 4.2” x 5.5” arch on luxe 380 GSM paper` },
          { text: tq`Premium white envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: SAVE_THE_DATE_IDS.minimalistBlackWhite.id,
        identifier: SAVE_THE_DATE_IDS.minimalistBlackWhite.identifier,
        shortName: SAVE_THE_DATE_IDS.minimalistBlackWhite.shortName,
        longName: SAVE_THE_DATE_IDS.minimalistBlackWhite.longName,
        costPerHundredString: '$3.93',
        videoUrl: 'https://player.vimeo.com/video/1006745470?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1923505038-5371188bd63a947e043838c4cfb959246b37092285e6748a634fee9575d8ebad-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/save-the-date-suites/minimalistBlackWhite/minimalistBlackWhite-1.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`This Foil and personalized photo combo is the perfect way to send out your Save the Dates in style.` },
          { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Save the Date card, 4.2” x 5.5” on 350 GSM heavyweight paper with gold foil` },
          { text: tq`Premium white envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: SAVE_THE_DATE_IDS.polaroid.id,
        identifier: SAVE_THE_DATE_IDS.polaroid.identifier,
        shortName: SAVE_THE_DATE_IDS.polaroid.shortName,
        longName: SAVE_THE_DATE_IDS.polaroid.longName,
        costPerHundredString: '$2.26',
        videoUrl: 'https://player.vimeo.com/video/1006745454?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1923505179-d7acabb7175f59d296b9ab8f2d55850b13325edbe75c7fb8ddbfa26ebc10cad4-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/save-the-date-suites/polaroid/polaroid-1.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`Announce your big day with your own photo captured on premium stock, creating a keepsake that lasts.` },
          { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Save the Date card, 4.2” x 5.5” on luxe 380 GSM paper` },
          { text: tq`Premium white envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 2,
      },
      {
        id: SAVE_THE_DATE_IDS.byron.id,
        identifier: SAVE_THE_DATE_IDS.byron.identifier,
        shortName: SAVE_THE_DATE_IDS.byron.shortName,
        longName: SAVE_THE_DATE_IDS.byron.longName,
        costPerHundredString: '$3.93',
        videoUrl: 'https://player.vimeo.com/video/1006744654?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1923504073-4a5fc458782ee655d7fcc6dacc66ad0fe59cf7fc04f55bed2bb7d02db3eda8bd-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/save-the-date-suites/byron/byron-1.jpg?v=2',
        descriptionHighlights: highlights(
          {
            text: tq`Inspired by the ${SAVE_THE_DATE_IDS.byron.shortName} shorelines in Australia, this arch shaped copper foil Save the Date is the perfect way to set the tone for your big day.`,
          },
          { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Save the Date card, 4.2” x 5.5” arch on luxe 380 GSM paper with copper foil` },
          { text: tq`Premium white envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: SAVE_THE_DATE_IDS.summerMontage.id,
        identifier: SAVE_THE_DATE_IDS.summerMontage.identifier,
        shortName: SAVE_THE_DATE_IDS.summerMontage.shortName,
        longName: SAVE_THE_DATE_IDS.summerMontage.longName,
        costPerHundredString: '$3.79',
        videoUrl: 'https://player.vimeo.com/video/1006748982?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1923509108-65dde415c0809e2f4bdd975b91e3895859f125f45e03a3e77e8ba3484528984f-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/save-the-date-suites/summerMontage/summerMontage-1.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`The Summer Nights double-arch Save the Date is the perfect way to announce your big day, personalized with your very own photo.` },
          { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Save the Date card, 4.2” x 5.5” double arch on luxe 380 GSM paper with white ink` },
          { text: tq`Premium white envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: SAVE_THE_DATE_IDS.delilah.id,
        identifier: SAVE_THE_DATE_IDS.delilah.identifier,
        shortName: SAVE_THE_DATE_IDS.delilah.shortName,
        longName: SAVE_THE_DATE_IDS.delilah.longName,
        costPerHundredString: '$3.30',
        videoUrl: 'https://player.vimeo.com/video/1006745036?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1923504570-0f3e7d691b23445a1d691f36e5c9e9db419fa7b90392f0af1bb17cb53970d78c-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/save-the-date-suites/delilah/delilah-1.jpg?v=2',
        descriptionHighlights: highlights(
          { text: tq`Where rustic charm meets minimalism, the ${SAVE_THE_DATE_IDS.delilah.shortName} arch-shaped save the dates perfectly set the tone for your special day.` },
          { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Save the Date card, 4.2” x 5.5” arch on 300 GSM cotton paper` },
          { text: tq`Premium white envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: SAVE_THE_DATE_IDS.floweret.id,
        identifier: SAVE_THE_DATE_IDS.floweret.identifier,
        shortName: SAVE_THE_DATE_IDS.floweret.shortName,
        longName: SAVE_THE_DATE_IDS.floweret.longName,
        costPerHundredString: '$3.30',
        videoUrl: 'https://player.vimeo.com/video/1006745029?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1923504483-d0831c1ac8d498058527e3674a3cd87e5e38b2d68ca59abcefda273d1bc9dace-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/save-the-date-suites/floweret/floweret-1.jpg?v=2',
        descriptionHighlights: highlights(
          {
            text: tq`The double-arch ${SAVE_THE_DATE_IDS.floweret.shortName} Save the Date features a graceful blend of blossom prints and typewriter-style fonts. It’s a charming way to announce your special day with a touch of timeless elegance.`,
          },
          { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Save the Date card, 4.2” x 5.5” double arch on 300 GSM cotton paper` },
          { text: tq`Premium white envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: SAVE_THE_DATE_IDS.summerNights.id,
        identifier: SAVE_THE_DATE_IDS.summerNights.identifier,
        shortName: SAVE_THE_DATE_IDS.summerNights.shortName,
        longName: SAVE_THE_DATE_IDS.summerNights.longName,
        costPerHundredString: '$3.79',
        videoUrl: 'https://player.vimeo.com/video/1006745437?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1923505003-181970c8e4225285b9451234dac82ed661e32e9e3d7ca492ff24060064420b00-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/save-the-date-suites/summerNights/summerNights-1.jpg?v=2',
        descriptionHighlights: highlights(
          {
            text: tq`${SAVE_THE_DATE_IDS.summerNights.shortName} Save the Dates are a perfect blend of blue and orange hues, a masterpiece crafted for those who dream of warm sands and the whisper of waves.`,
          },
          { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Save the Date card, 4.2” x 5.5” arch on luxe 380 GSM paper` },
          { text: tq`Premium white envelopes` },
          sharedEnvelopeAddressing,
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 6,
      },
      {
        id: SAVE_THE_DATE_IDS.clairDeLune.id,
        identifier: SAVE_THE_DATE_IDS.clairDeLune.identifier,
        shortName: SAVE_THE_DATE_IDS.clairDeLune.shortName,
        longName: SAVE_THE_DATE_IDS.clairDeLune.longName,
        costPerHundredString: '$3.27',
        videoUrl: 'https://player.vimeo.com/video/1006745058?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479',
        videoThumbnailUrl: 'https://i.vimeocdn.com/video/1923504604-6e59b3bf477e599bd64165ac6c2cce872523e6ab8027b2cbceeb0e19d2d36037-d_1280x720?r=pad',
        imageUrl: 'https://withjoy.com/assets/public/messaging/partnerships/paperlust/save-the-date-suites/clairDeLune/clair_de_lune-3.jpg?v=2',
        descriptionHighlights: highlights(
          {
            text: tq`The ${SAVE_THE_DATE_IDS.clairDeLune.shortName} double-layer Save the Dates are a delightful blend of playfulness and elegance. The top layer, printed in white ink on delicate vellum, overlays your personalized photo on the second layer, creating a beautiful keepsake.`,
          },
          { text: tq`Upgrade to smooth magnetic backing for a fridge-friendly keepsake.` },
        ),
        suiteHighlights: highlights(
          sharedPersonalization,
          { text: tq`Save the Date card, 4.2” x 5.5” on 350 GSM heavyweight paper` },
          { text: tq`Sheer overlay, 4.2” x 5.5” on 180 GSM vellum with white ink` },
          { text: tq`Premium white envelopes` },
          sharedEnvelopeAddressing,
          { text: tq`Hole punch (ribbon not included)` },
          { text: tq`Gold fastener` },
          sharedNotIncluded,
        ),
        printingBusinessDaysQuantity: 2,
      },
    ].sort(() => Math.random() - 0.5);

    return {
      [StationeryTemplateCategoryEnum.invitation]: invitationOfferings,
      [StationeryTemplateCategoryEnum.saveTheDate]: saveTheDateOfferings,
    };
  }, [tq]);

  const getConciergeCategoryFromDesignSetName = useCallback(
    (designSetName: string) => {
      return Object.keys(productOfferings).find(key =>
        productOfferings[key as ConciergeServiceCategory].some(offering => offering.identifier === designSetName),
      ) as ConciergeServiceCategory;
    },
    [productOfferings],
  );

  const getProductOfferingFromDesignSetName = useCallback(
    (designSetName: string) => {
      return Object.values(productOfferings)
        .flat()
        .find(offering => offering.identifier === designSetName) as ProductOffering;
    },
    [productOfferings],
  );

  const getProductOfferingFromCategoryAndId = useCallback(
    (category: ConciergeServiceCategory, id: string) => {
      return productOfferings[category].find(offering => offering.id === id);
    },
    [productOfferings],
  );

  return {
    productOfferings,
    getConciergeCategoryFromDesignSetName,
    getProductOfferingFromDesignSetName,
    getProductOfferingFromCategoryAndId,
  };
};
